import {Shift, Template} from '@cquential/api';

export default class WorkloadTree {
    public static create(shifts: Record<number, Shift>): Record<number, number[]> {
        // first: build the tree for shift templates, AND build a mapping of template -> shiftIds
        const templateToShiftMap: Record<number, number[]> = {}; // template -> shift ids
        const templateTree: Record<number, number[]> = {}; // template -> other template ids
        this.fillTemplateMaps(shifts, templateToShiftMap, templateTree);

        return this.createShiftTree(shifts, templateToShiftMap, templateTree); // shift -> other shift ids of same roster
    }

    /**
     *
     * @param shifts
     * @param templateToShiftMap
     * @param templateTree
     */
    private static fillTemplateMaps(shifts: Record<number, Shift>, templateToShiftMap: Record<number, number[]>, templateTree: Record<number, number[]>): void {
        for (const k in shifts) {
            const shiftId: number = parseInt(k, 10);
            const template: Template = shifts[shiftId].template!;

            // if the template is not mapped to any shift, this is the first time we encounter it
            if (!templateToShiftMap[template.id]) {
                // add any parents
                if (template.orParentTemplateId) {
                    // if this template is an or parent of another, fulfilling any shift of this template, also fulfills
                    // any shift with the parent template
                    templateTree[template.id] ??= [];
                    templateTree[template.id].push(template.orParentTemplateId);
                }

                if (template.andParentTemplateId) {
                    // if this template is an and child (it's called and parent because), fulfilling the other shift,
                    // also fulfills this shift
                    // they essentially work the same, but the relation is reversed so you can use one to create
                    // many-to-one relations, and the other to create one-to-many relations
                    // if it were possible to define multiple or-parents, and-parents would not be needed
                    templateTree[template.andParentTemplateId] ??= [];
                    templateTree[template.andParentTemplateId].push(template.id);
                }
            }

            // add template to shift mapping:
            templateToShiftMap[template.id] ??= [];
            templateToShiftMap[template.id].push(shiftId);
        }
    }

    private static createShiftTree(shifts: Record<number, Shift>, templateToShiftMap: Record<number, number[]>, templateTree: Record<number, number[]>): Record<number, number[]> {
        const shiftTree: Record<number, number[]> = {};
        for (const k in templateTree) {
            const parentTemplateId: number = parseInt(k, 10);
            for (const childTemplateId of templateTree[k]) {
                this.addTemplateRelation(shifts, templateToShiftMap, shiftTree, parentTemplateId, childTemplateId);
            }
        }

        return shiftTree;
    }

    private static addTemplateRelation(shifts: Record<number, Shift>, templateToShiftMap: Record<number, number[]>, shiftTree: Record<number, number[]> = {}, parentTemplateId: number, childTemplateId: number): void {
        if (!(parentTemplateId in templateToShiftMap) || !(childTemplateId in templateToShiftMap)) {
            // if either template does not have shifts, you can never fulfill it
            return;
        }
        for (const parentShiftId of templateToShiftMap[parentTemplateId]) {
            for (const childShiftId of templateToShiftMap[childTemplateId]) {
                if (shifts[parentShiftId].rosterId === shifts[childShiftId].rosterId) {
                    // if the roster is different there is no need to add the relationship
                    shiftTree[parentShiftId] ??= [];
                    shiftTree[parentShiftId].push(childShiftId);
                }
            }
        }
    }
}
